import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { setLoader } from "../../../Slices/loaderSlice";
import { S3ACCESSURL } from "../../../databucket";

// Self-contained, modern Clients directory. All styling is scoped under
// `.bpcl` so it neither depends on the global theme nor leaks into other
// screens. Cards are dense; each client gets a deterministic gradient avatar
// derived from its name for fast visual scanning.

const FILTERS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

// Deterministic hue from a string → stable, distinct avatar gradient.
function hueFromString(str) {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) {
    h = (h * 31 + str.charCodeAt(i)) % 360;
  }
  return h;
}

const Clients = () => {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState("active");
  const [search, setSearch] = useState("");
  const clients = useSelector((state) => state.clients.clients);
  const loading = useSelector((state) => state.clients.loading);

  useEffect(() => {
    dispatch(setLoader(loading));
  }, [dispatch, loading]);

  const getInitials = (name) => {
    const names = (name || "").trim().split(/\s+/);
    if (names.length > 1) {
      return `${names[0].charAt(0)}${names[1].charAt(0)}`.toUpperCase();
    }
    return names[0] ? names[0].charAt(0).toUpperCase() : "N/A";
  };

  const filteredClients = useMemo(
    () =>
      clients?.filter((client) => {
        const matchesStatus =
          filter === "all" || client?.status?.toLowerCase() === filter;
        const matchesSearch = client?.clientName
          ?.toLowerCase()
          .includes(search.toLowerCase());
        return matchesStatus && matchesSearch;
      }) || [],
    [clients, filter, search]
  );

  return (
    <div>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Breadcrumbs
            maintitle="Clients"
            title="Dashboard"
            subtitle="Clients"
            modal="#add_client"
            name="Add Client"
            Linkname="/clients"
            Linkname1="/clients-list"
            view="grid"
          />

          <div className="bpcl">
            <style>{BPCL_STYLES}</style>

            {/* Controls */}
            <div className="bpcl-controls">
              <div className="bpcl-segmented" role="tablist" aria-label="Status filter">
                {FILTERS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    role="tab"
                    aria-selected={filter === f.value}
                    className={`bpcl-chip ${filter === f.value ? "is-active" : ""}`}
                    onClick={() => setFilter(f.value)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="bpcl-search">
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                  <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="1.8" />
                  <path d="m20 20-3.2-3.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                <input
                  placeholder="Search clients…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search ? (
                  <button type="button" className="bpcl-clear" aria-label="Clear" onClick={() => setSearch("")}>
                    ×
                  </button>
                ) : null}
              </div>

              <span className="bpcl-count">{filteredClients.length} clients</span>
            </div>

            {/* Grid */}
            {filteredClients.length === 0 ? (
              <div className="bpcl-empty">
                <div className="bpcl-empty-icon">🔍</div>
                <div className="bpcl-empty-title">No clients found</div>
                <div className="bpcl-empty-body">
                  {search ? "Try a different name or clear the search." : "No clients match this filter."}
                </div>
              </div>
            ) : (
              <div className="bpcl-grid">
                {filteredClients.map((client) => {
                  const isActive = client?.status?.toLowerCase() === "active";
                  const hue = hueFromString(client?.clientName || "");
                  return (
                    <Link
                      key={client?.id}
                      to={`/clients/${client?.id}`}
                      className="bpcl-card"
                    >
                      <div className="bpcl-card-top">
                        <span className="bpcl-avatar-wrap">
                          {client?.image ? (
                            <img className="bpcl-avatar-img" alt="" src={S3ACCESSURL + client.image} />
                          ) : (
                            <span
                              className="bpcl-avatar"
                              style={{
                                background: `linear-gradient(135deg, hsl(${hue} 72% 56%), hsl(${(hue + 38) % 360} 70% 44%))`,
                              }}
                            >
                              {getInitials(client?.clientName)}
                            </span>
                          )}
                          <span
                            className="bpcl-status"
                            style={{ background: isActive ? "#16a34a" : "#dc2626" }}
                            title={isActive ? "Active" : "Inactive"}
                          />
                        </span>
                        <span className="bpcl-arrow" aria-hidden>→</span>
                      </div>

                      <div className="bpcl-name" title={client?.clientName}>
                        {client?.clientName || "Unnamed client"}
                      </div>

                      <div className="bpcl-meta">
                        {client?.department ? (
                          <span className="bpcl-tag">{client.department}</span>
                        ) : null}
                        <span className={`bpcl-dot-label ${isActive ? "on" : "off"}`}>
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const BPCL_STYLES = `
.bpcl { --bpcl-ink:#0f172a; --bpcl-muted:#64748b; --bpcl-line:#e8ecf2; --bpcl-accent:#2563eb; color:var(--bpcl-ink); }
.bpcl-controls { display:flex; align-items:center; gap:12px; margin:4px 0 18px; flex-wrap:wrap; }
.bpcl-segmented { display:inline-flex; gap:2px; padding:3px; background:#f1f4f9; border:1px solid var(--bpcl-line); border-radius:999px; }
.bpcl-chip { border:0; background:transparent; color:var(--bpcl-muted); font-size:13px; font-weight:600; padding:6px 14px; border-radius:999px; cursor:pointer; transition:all .15s ease; }
.bpcl-chip:hover { color:var(--bpcl-ink); }
.bpcl-chip.is-active { background:#fff; color:var(--bpcl-accent); box-shadow:0 1px 2px rgba(16,24,40,.08); }
.bpcl-search { display:inline-flex; align-items:center; gap:8px; background:#fff; border:1px solid var(--bpcl-line); border-radius:10px; padding:0 12px; height:38px; min-width:240px; color:var(--bpcl-muted); transition:border-color .15s, box-shadow .15s; }
.bpcl-search:focus-within { border-color:var(--bpcl-accent); box-shadow:0 0 0 3px rgba(37,99,235,.14); }
.bpcl-search input { border:0; outline:0; background:transparent; font-size:14px; color:var(--bpcl-ink); width:100%; }
.bpcl-clear { border:0; background:transparent; color:var(--bpcl-muted); font-size:18px; line-height:1; cursor:pointer; padding:0 2px; }
.bpcl-count { margin-left:auto; font-size:13px; color:var(--bpcl-muted); font-weight:500; }

.bpcl-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(184px, 1fr)); gap:12px; }

.bpcl-card { display:flex; flex-direction:column; gap:10px; padding:14px; background:#fff; border:1px solid var(--bpcl-line); border-radius:14px; text-decoration:none; color:inherit; box-shadow:0 1px 2px rgba(16,24,40,.04); transition:transform .15s ease, box-shadow .2s ease, border-color .15s ease; }
.bpcl-card:hover { transform:translateY(-3px); box-shadow:0 12px 24px -10px rgba(16,24,40,.22); border-color:#d3dbe7; color:inherit; }

.bpcl-card-top { display:flex; align-items:flex-start; justify-content:space-between; }
.bpcl-avatar-wrap { position:relative; display:inline-flex; }
.bpcl-avatar, .bpcl-avatar-img { width:46px; height:46px; border-radius:13px; display:inline-flex; align-items:center; justify-content:center; font-size:15px; font-weight:700; color:#fff; object-fit:cover; box-shadow:0 4px 10px -3px rgba(16,24,40,.25); }
.bpcl-avatar-img { background:#f1f4f9; }
.bpcl-status { position:absolute; bottom:-2px; right:-2px; width:12px; height:12px; border-radius:50%; border:2.5px solid #fff; }
.bpcl-arrow { color:#cbd5e1; font-size:16px; transition:transform .15s ease, color .15s ease; }
.bpcl-card:hover .bpcl-arrow { color:var(--bpcl-accent); transform:translateX(3px); }

.bpcl-name { font-size:14.5px; font-weight:650; letter-spacing:-.01em; line-height:1.3; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.bpcl-meta { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.bpcl-tag { font-size:11px; font-weight:600; color:#475569; background:#f1f4f9; border:1px solid var(--bpcl-line); padding:2px 8px; border-radius:999px; max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.bpcl-dot-label { font-size:11px; font-weight:600; display:inline-flex; align-items:center; gap:5px; }
.bpcl-dot-label::before { content:""; width:6px; height:6px; border-radius:50%; }
.bpcl-dot-label.on { color:#16a34a; } .bpcl-dot-label.on::before { background:#16a34a; }
.bpcl-dot-label.off { color:#dc2626; } .bpcl-dot-label.off::before { background:#dc2626; }

.bpcl-empty { text-align:center; padding:64px 20px; color:var(--bpcl-muted); background:#fff; border:1px dashed #d3dbe7; border-radius:16px; }
.bpcl-empty-icon { font-size:26px; margin-bottom:8px; }
.bpcl-empty-title { font-size:16px; font-weight:700; color:var(--bpcl-ink); }
.bpcl-empty-body { font-size:13px; margin-top:4px; }

@media (max-width:640px){ .bpcl-grid{ grid-template-columns:repeat(auto-fill, minmax(150px,1fr)); } .bpcl-count{ width:100%; margin-left:0; } }
`;

export default Clients;
